import { type UserGroup } from '#app/__generated__/relay/AdminActionsQuery.graphql.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { springAnimation } from '#app/utilities/animation.js';
import { CellLayout } from '../CellLayout.js';
import {
  AccessTypeLabel,
  AccessTypeLayout,
  AccessTypePromotion,
  AccessTypeValues,
  ContraProText,
  Grid,
  LabelAndPromo,
  StyledButton,
  ToggleButton,
} from './ContraProMenu.styles.js';
import { ChevronDownIcon } from '@contra/icons/icons';
import { graphql } from '@contra/relay';
import { type ReactNode, useCallback, useState } from 'react';

const validUserGroups: UserGroup[] = ['CONTRA_PRO_OLD_PRICE_GROUP'];
type GrantAccessType = {
  label: string;
  promotion?: string;
  userGroup: ValidUserGroups;
};
type ValidUserGroups = (typeof validUserGroups)[number];

const grantAccessTypes: GrantAccessType[] = [
  {
    label: '$12/mo price',
    promotion: '$17 off forever to bring users in line with old pricing',
    userGroup: 'CONTRA_PRO_OLD_PRICE_GROUP',
  },
];

export const ContraProMenu = ({
  children,
  onMutationSuccess,
  userGroups,
  userId,
}: {
  readonly children?: ReactNode;
  readonly onMutationSuccess?: (
    userGroup: ValidUserGroups,
    action: 'add' | 'remove',
  ) => void;
  readonly userGroups: readonly UserGroup[];
  readonly userId: string;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const hasPermissions = useHasUserFeatures(['MANAGE_USER_GROUPS']);
  const toast = useToast();

  const toggleMenu = useCallback(() => {
    setIsOpen((currentState) => !currentState);
  }, []);

  const [adminAddUserToGroup] = useContraMutation(graphql`
    mutation ContraProMenuAdminAddUserToGroupMutation(
      $input: AdminAddUserToGroupInput!
    ) {
      adminAddUserToGroup(input: $input) {
        errors {
          __typename
          message
        }
        adminUserSearchResult {
          id
          userGroups
        }
      }
    }
  `);

  const [adminRemoveUserFromGroup] = useContraMutation(graphql`
    mutation ContraProMenuAdminRemoveUserFromGroupMutation(
      $input: AdminRemoveUserFromGroupInput!
    ) {
      adminRemoveUserFromGroup(input: $input) {
        errors {
          __typename
          message
        }
        adminUserSearchResult {
          id
          userGroups
        }
      }
    }
  `);

  const onClickRevokeGroup = (userGroup: ValidUserGroups) => {
    adminRemoveUserFromGroup({
      onCompleted: (errors) => {
        const { apiErrors, payloadErrors } = errors;

        if (payloadErrors) {
          toast.error(
            'An unexpected error occurred. Our team has been notified.',
          );

          return;
        }

        if (apiErrors) {
          for (const apiError of apiErrors) {
            toast.error(apiError.message);
          }

          return;
        }

        toast.success('Early adopter access revoked');
        onMutationSuccess?.(userGroup, 'remove');
      },
      onError: () => {
        toast.error(
          'Oops! Something went wrong. Our team has been notified. Please try again.',
        );
      },
      variables: {
        input: {
          userGroup,
          userId,
        },
      },
    });
  };

  const onClickGrantGroup = (userGroup: ValidUserGroups) => {
    adminAddUserToGroup({
      onCompleted: (errors) => {
        const { apiErrors, payloadErrors } = errors;

        if (payloadErrors) {
          toast.error(
            'An unexpected error occurred. Our team has been notified.',
          );

          return;
        }

        if (apiErrors) {
          for (const apiError of apiErrors) {
            toast.error(apiError.message);
          }

          return;
        }

        toast.success('Early adopter access granted!');
        const otherGroups = validUserGroups.filter(
          (group) => group !== userGroup,
        );

        for (const group of otherGroups) {
          adminRemoveUserFromGroup({
            onCompleted: () => {},
            onError: () => {},
            variables: {
              input: {
                userGroup: group,
                userId,
              },
            },
          });
        }

        onMutationSuccess?.(userGroup, 'add');
      },
      onError: () => {
        toast.error(
          'Oops! Something went wrong. Our team has been notified. Please try again.',
        );
      },
      variables: {
        input: {
          userGroup,
          userId,
        },
      },
    });
  };

  return hasPermissions ? (
    <Menu
      isOpen={isOpen}
      toggleMenu={toggleMenu}
    >
      <Menu.Reference>
        <ToggleButton
          as="button"
          onClick={toggleMenu}
          type="button"
        >
          <CellLayout>{children}</CellLayout>
          <animated.div
            animate={{
              rotate: isOpen ? 180 : 0,
            }}
            initial={false}
            transition={springAnimation.fast}
          >
            <ChevronDownIcon size={24} />
          </animated.div>
        </ToggleButton>
      </Menu.Reference>
      <Menu.Panel>
        <AccessTypeLayout>
          <ContraProText>Contra Pro Access Groups</ContraProText>
          <Grid>
            {grantAccessTypes.map((accessType) => {
              const hasGroup = userGroups.includes(accessType.userGroup);
              const buttonLabel = hasGroup ? 'Revoke' : 'Grant';

              return (
                <div key={`contra-pro-access-${accessType.label}`}>
                  <LabelAndPromo>
                    <AccessTypeLabel>{accessType.label}</AccessTypeLabel>
                    <AccessTypePromotion>
                      {accessType?.promotion ?? 'Regular Pricing'}
                    </AccessTypePromotion>
                  </LabelAndPromo>
                  <AccessTypeValues>
                    <StyledButton
                      onClick={() =>
                        hasGroup
                          ? onClickRevokeGroup(accessType.userGroup)
                          : onClickGrantGroup(accessType.userGroup)
                      }
                    >
                      {buttonLabel}
                    </StyledButton>
                  </AccessTypeValues>
                </div>
              );
            })}
          </Grid>
        </AccessTypeLayout>
      </Menu.Panel>
    </Menu>
  ) : null;
};
